import React, { useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/form/BaseButton';
import { Row } from 'components/layout/Row';
import { Column } from 'components/layout/Column';
import Modal from 'components/form/Modal';
import CancelIcon from '@images/IconHeader/cancel.svg';
import { useQuery } from '@apollo/client';
import { BridgeQuery } from '../../query/bridge';

// rileva l'OS del client per filtrare le build (MAC/WIN); null se non riconosciuto (es. Linux)
function detectUserOS(): 'MAC' | 'WIN' | null {
  const uaData = (navigator as any).userAgentData;
  const platform = (uaData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase();
  if (platform.includes('win')) return 'WIN';
  if (platform.includes('mac')) return 'MAC';
  return null;
}

// normalizzo opSystem del backend ('WIN' | 'MAC' | 'MAC_Mx') in un tag stabile
function buildTag(opSystem: string): 'WIN' | 'MAC' | 'MAC_MX' {
  const s = (opSystem || '').toUpperCase();
  if (s.includes('MX')) return 'MAC_MX'; // apple silicon (es. "MAC_Mx")
  if (s.includes('MAC')) return 'MAC'; // mac intel
  return 'WIN';
}

// build da mostrare di default in base all'OS; null = OS sconosciuto → mostra tutto
function getPreferredTags(): Array<'WIN' | 'MAC' | 'MAC_MX'> | null {
  const os = detectUserOS();
  if (os === 'WIN') return ['WIN'];
  if (os === 'MAC') return ['MAC', 'MAC_MX']; // entrambe le architetture: sceglie l'utente
  return null;
}

export default function BridgeWidget() {
  const { loading, error, data } = useQuery(BridgeQuery, {
    fetchPolicy: 'no-cache',
  });
  const { t } = useTranslation();
  const preferredTags = useMemo(() => getPreferredTags(), []);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false);

  // etichetta piattaforma leggibile
  const osLabel = (opSystem: string) => {
    switch (buildTag(opSystem)) {
      case 'WIN': return t('bridge.dialog.osWindows', 'Windows');
      case 'MAC_MX': return t('bridge.dialog.osMacApple', 'Mac (Apple Silicon)');
      default: return t('bridge.dialog.osMacIntel', 'Mac (Intel)');
    }
  };

  function getRequirements(version: string) {
    const flatVersion = version.split('.').join('');
    if (parseInt(flatVersion) > 840) {
      return {
        WIN: t('bridge.dialog.windowsArchitecture', 'os WIN 10 - 64 bit architecture, administrator privileges'),
        MAC: t('bridge.dialog.macArchitectureAfter11', 'os version 11 and above'),
      };
    }
    return {
      WIN: t('bridge.dialog.windowsArchitecture', 'os WIN 10 - 64 bit architecture, administrator privileges'),
      MAC: t('bridge.dialog.macArchitecture', 'os version > 10.13'),
    };
  }

  function bridgeOrder(arr: any[]) {
    const macs: any[] = [];
    const wins: any[] = [];
    arr.forEach((item) => {
      if (buildTag(item.opSystem) === 'WIN') {
        wins.push(item);
      } else {
        macs.push(item); // MAC (intel) e MAC_MX (apple silicon)
      }
    });
    const orderedArr: any[] = [];
    const maxLength = Math.max(macs.length, wins.length);
    // alterno una versione mac e una win
    for (let i = 0; i < maxLength; i++) {
      if (i < macs.length) orderedArr.push(macs[i]);
      if (i < wins.length) orderedArr.push(wins[i]);
    }
    return orderedArr;
  }

  const orderedData = data?.res ? bridgeOrder(data?.res) : [];
  // di default mostro solo le build per OS + architettura rilevati; se incerto/sconosciuto mostro di più
  const visibleData = preferredTags && !showAll
    ? orderedData.filter((item) => preferredTags.includes(buildTag(item.opSystem)))
    : orderedData;
  const hasOtherOS = preferredTags
    ? orderedData.some((item) => !preferredTags.includes(buildTag(item.opSystem)))
    : false;

  const handleDownload = (downloadUrl: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadUrl;
    link.style.display = 'none'; // hide URL
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <BaseButton
        startIcon={<GetAppIcon />}
        onClick={() => setOpen(true)}
      >
        bridge
      </BaseButton>
      <Modal
        buttonActionClassName="button-action-svg-fill-white"
        title={t('dialog.title.download', 'download')}
        buttonColor="secondary"
        buttonAction={t('drawer.actions.close', 'close')}
        onClose={() => setOpen(false)}
        visible={open}
        onClick={() => setOpen(false)}
        noCancel
        icon={<CancelIcon />}
      >
        <Box>
          {loading && <Box className="bridge-widget-message">{t('bridge.dialog.loading')}</Box>}
          {!loading && error && <Box className="bridge-widget-message">{t('bridge.dialog.error')}</Box>}
          {!loading && !error && (
            <Row>
              {visibleData.map((item) => (
                <Column xs={12} sm={6} key={item.opSystem + item.version}>
                  <Box className="bridge-widget-download">
                    <span className="bridge-widget-platform">{osLabel(item.opSystem)}</span>
                    <span className="bridge-widget-sub">
                      {`${t('bridge.dialog.version', 'ver')} ${item.version} ${t('bridge.dialog.for', 'for')} ${item.system.join(' ').toLowerCase()}`}
                    </span>
                    <BaseButton
                      buttonSize="sm"
                      onClick={() => handleDownload(item.downloadUrl)}
                      noMargins
                      startIcon={<GetAppIcon />}
                    >
                      {`${t('bridge.dialog.downloadFor', 'download for')} ${buildTag(item.opSystem) === 'WIN' ? 'Windows' : 'Mac'}`}
                    </BaseButton>
                    <span className="bridge-widget-requirements">
                      {`${t('bridge.dialog.minRequirements', 'Minimal requirements')}: ${getRequirements(item.version)[buildTag(item.opSystem) === 'WIN' ? 'WIN' : 'MAC']}`}
                    </span>
                  </Box>
                </Column>
              ))}
            </Row>
          )}
          {!loading && !error && hasOtherOS && (
            <Box mt={2} display="flex" justifyContent="center">
              <span
                className="bridge-widget-show-all"
                role="button"
                tabIndex={0}
                onClick={() => setShowAll((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setShowAll((prev) => !prev);
                }}
              >
                {showAll
                  ? t('bridge.dialog.showForMySystem', 'show versions for my system only')
                  : t('bridge.dialog.showAllSystems', 'show versions for all systems')}
              </span>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
}
